import { useState, useMemo, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, ArrowUpRight, ArrowDownRight, CreditCard, Download, Upload, GripVertical, ChevronLeft, ChevronRight, Copy, Save, Lock, LockOpen, Zap, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import BudgetDialog from "@/components/BudgetDialog";
import BudgetImportDialog from "@/components/BudgetImportDialog";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { useDisplay } from "@/context/DisplayContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fmtDate } from "@/utils/dateFormat";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";

const fmt = (v) => `£${Math.abs(Number(v)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Build tag — bump this string every time the row-edit logic changes so the
// user can verify in DevTools which bundle their VPS is actually serving.
// If the console / DOM doesn't show this tag, the deployed build is stale.
const BUDGET_BUILD_TAG = "budget-canEdit-v2 (2026-02)";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function formatMonthDisplay(monthStr) {
  if (!monthStr) return "";
  const [y, m] = monthStr.split("-");
  return `${MONTH_NAMES[parseInt(m)-1]} ${y}`;
}

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split("-").map(Number);
  let newM = m + delta;
  let newY = y;
  if (newM > 12) { newM = 1; newY++; }
  if (newM < 1) { newM = 12; newY--; }
  return `${newY}-${String(newM).padStart(2, "0")}`;
}

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2, "0")}`;
}

const categoryConfig = {
  income: { label: "Income", icon: ArrowUpRight, color: "text-green-800", iconColor: "text-green-600", bg: "bg-green-50/30" },
  expense: { label: "Bill/Expense", icon: ArrowDownRight, color: "text-red-800", iconColor: "text-red-600", bg: "" },
  debt_payment: { label: "Debt Payment", icon: CreditCard, color: "text-blue-800", iconColor: "text-blue-600", bg: "bg-blue-50/20" },
};

function SortableBudgetRow({ b, config, Icon, fmt, handleEdit, handleDelete, canEdit, dndDisabled, selected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: b.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isIncome = b.category === "income";
  return (
    <tr ref={setNodeRef} style={style} data-testid={`budget-row-${b.id}`} className={config.bg}>
      <td className="text-center w-8">
        {canEdit && (
          <input
            type="checkbox"
            data-testid={`select-budget-${b.id}`}
            checked={!!selected}
            onChange={(e) => onToggleSelect(b.id, e.target.checked)}
            className="cursor-pointer"
            aria-label={`Select ${b.description}`}
          />
        )}
      </td>
      <td className="text-center w-8">
        {canEdit && !dndDisabled && (
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-gray-100 rounded-sm" data-testid={`drag-budget-${b.id}`}>
            <GripVertical className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
          </button>
        )}
      </td>
      <td className="font-mono-data text-xs text-gray-600 whitespace-nowrap">{fmtDate(b.date)}</td>
      <td className={`whitespace-nowrap ${config.color}`}>
        <div className="flex items-center gap-1.5">
          <Icon className={`w-3.5 h-3.5 ${config.iconColor} shrink-0`} strokeWidth={1.5} />
          <span>{b.description}</span>
        </div>
      </td>
      <td className="text-center">
        <Badge className={`badge-pill text-[10px] px-2 py-0 font-mono-data border ${
          b.category === "income" ? "border-green-300 text-green-700 bg-green-50" :
          b.category === "debt_payment" ? "border-blue-300 text-blue-700 bg-blue-50" :
          "border-gray-300 text-gray-600 bg-gray-50"
        }`} variant="outline">{config.label}</Badge>
      </td>
      <td className="numeric">{b.budgeted > 0 ? fmt(b.budgeted) : "-"}</td>
      <td className={`numeric font-medium ${b.category === "income" ? "text-green-700" : b.category === "debt_payment" ? "text-blue-700" : "text-red-700"}`}>
        {b.actual > 0 ? fmt(b.actual) : "-"}
      </td>
      <td className={`numeric text-xs ${b.variance > 0 ? "text-red-600 font-semibold" : b.variance < 0 ? "text-green-600 font-semibold" : "text-gray-300"}`}>
        {b.variance !== 0 ? (b.category === "income" ? (b.variance > 0 ? "+" : "") : (b.variance < 0 ? "-" : "+")) + fmt(b.variance) : "-"}
      </td>
      <td className={`numeric font-medium ${b.running_balance >= 0 ? "text-gray-900" : "text-red-700"}`}>
        {b.running_balance >= 0 ? "" : "-"}{fmt(b.running_balance)}
      </td>
      <td className="text-xs whitespace-nowrap">
        {b.debt_name ? <span className="text-blue-600 font-medium">{b.debt_name}</span> : <span className="text-gray-300">-</span>}
      </td>
      <td className="numeric text-xs">{b.debt_balance > 0 ? fmt(b.debt_balance) : "-"}</td>
      <td className="text-center">
        {canEdit && (
          <div className="flex items-center justify-center gap-1">
            <button data-testid={`edit-budget-${b.id}`} onClick={() => handleEdit(b)} className="p-1 hover:bg-gray-100 rounded-sm transition-colors">
              <Pencil className="w-3.5 h-3.5 text-gray-500 action-icon" strokeWidth={1.5} />
            </button>
            {isIncome ? (
              <span className="p-1" title="Income row cannot be deleted">
                <Lock className="w-3.5 h-3.5 text-gray-300" strokeWidth={1.5} />
              </span>
            ) : (
              <button data-testid={`delete-budget-${b.id}`} onClick={() => handleDelete(b)} className="p-1 hover:bg-red-50 rounded-sm transition-colors">
                <Trash2 className="w-3.5 h-3.5 text-red-500 action-icon" strokeWidth={1.5} />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function MonthlyBudgetPage({ debts, onRefresh, api }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [budget, setBudget] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [dateSort, setDateSort] = useState(null); // null=manual(DnD), "asc", "desc"
  // Past-month soft-lock: viewing is read-only by default, but the user
  // can flip an "Unlock for Editing" toggle (with warning) to correct
  // historical mistakes. Resets every time the selected month changes.
  const [pastEditUnlocked, setPastEditUnlocked] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  // Bulk multi-select state — row IDs selected via the new leftmost
  // checkbox column. Cleared whenever the user navigates months or
  // after a successful bulk-delete.
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const currentMonth = getCurrentMonth();
  const isPastMonth = selectedMonth < currentMonth;
  const isReadOnly = isPastMonth && !pastEditUnlocked;
  // Explicit edit gate for rows: a row is editable whenever the month is
  // current OR the user has unlocked the past month. This is intentionally
  // independent from `isReadOnly` so bank-synced rows can never end up in a
  // "locked even after unlock" state.
  const canEditRows = !isPastMonth || pastEditUnlocked;
  const debtNames = debts.map((d) => d.name);
  const { theme } = useDisplay();

  // Re-lock whenever the user navigates to a different month so the
  // warning is acknowledged once per month, not once per session.
  useEffect(() => { setPastEditUnlocked(false); setSelectedRows([]); }, [selectedMonth]);

  // Diagnostic: log every time the edit-gate state changes so the user
  // can confirm in DevTools that `canEdit` flips to true after unlock.
  // Temporary — safe to remove once the bug is confirmed fixed in the wild.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[VAULT/BUDGET]", BUDGET_BUILD_TAG, {
      selectedMonth,
      currentMonth,
      isPastMonth,
      pastEditUnlocked,
      canEditRows,
      rowCount: budget.length,
      firstRowId: budget[0]?.id,
    });
  }, [selectedMonth, currentMonth, isPastMonth, pastEditUnlocked, canEditRows, budget]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const fetchBudget = useCallback(async (month) => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/budget?month=${month}`);
      setBudget(res.data);
    } catch { setBudget([]); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchBudget(selectedMonth); }, [selectedMonth, fetchBudget]);

  const refresh = useCallback(async () => {
    await fetchBudget(selectedMonth);
    await onRefresh();
  }, [fetchBudget, selectedMonth, onRefresh]);

  const exportBudgetCSV = () => {
    const header = "Date,Description,Category,Budgeted,Actual,Debt Link";
    const rows = budget.map(b => `"${b.date}","${b.description}","${b.category}",${b.budgeted},${b.actual},"${b.debt_name || ""}"`);
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `the-vault-budget-${selectedMonth}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleAdd = () => { setEditEntry(null); setDialogOpen(true); };
  const handleEdit = (entry) => { setEditEntry(entry); setDialogOpen(true); };

  const handleSave = async (data) => {
    try {
      if (editEntry) {
        await axios.put(`${api}/budget/${editEntry.id}`, data);
        toast.success("Entry updated");
      } else {
        await axios.post(`${api}/budget`, data);
        toast.success("Entry added");
      }
      setDialogOpen(false);
      await refresh();
    } catch (e) { toast.error("Failed to save entry"); }
  };

  const handleDelete = (entry) => {
    setDeleteTarget(entry);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${api}/budget/${deleteTarget.id}`);
      toast.success("Entry deleted");
      setDeleteTarget(null);
      await refresh();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = budget.findIndex(b => b.id === active.id);
    const newIndex = budget.findIndex(b => b.id === over.id);
    const newOrder = arrayMove(budget, oldIndex, newIndex).map(b => b.id);
    try { await axios.put(`${api}/budget/reorder`, { order: newOrder }); await refresh(); }
    catch { toast.error("Failed to reorder"); }
  };

  const handleCopyFromLast = async () => {
    try {
      const res = await axios.post(`${api}/budget/copy-from-last?month=${selectedMonth}`);
      // New backend contract: { copied, skipped }. Older payloads use
      // `updated` — fall back so this still works during VPS rollout.
      const copied = res.data?.copied ?? res.data?.updated ?? 0;
      const skipped = res.data?.skipped ?? 0;
      toast.success(`Copied ${copied} new entries, skipped ${skipped} already present`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to copy"); }
  };

  const handleSaveTemplate = () => {
    // Open the two-choice dialog instead of silently snapshotting the
    // current month — spec requires the user to pick between
    // "Save current entries" and "Start blank template".
    setTemplateDialogOpen(true);
  };

  const saveTemplateAs = async (mode) => {
    try {
      const res = await axios.post(`${api}/budget/save-template?mode=${mode}`);
      if (mode === "blank") {
        toast.success("Started a blank template — add entries to build your recurring budget");
      } else {
        toast.success(`Saved ${res.data.saved} items as budget template`);
      }
      setTemplateDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save template"); }
  };

  // Bulk multi-select handlers ----------------------------------------------
  const toggleSelectRow = useCallback((id, checked) => {
    setSelectedRows(prev => checked
      ? (prev.includes(id) ? prev : [...prev, id])
      : prev.filter(x => x !== id));
  }, []);

  const toggleSelectAll = (checked) => {
    if (checked) {
      // Income rows can't be deleted individually (spec elsewhere), so
      // we still let the user select them — backend will simply delete
      // whatever IDs come in. Pure UX: select-all targets the visible
      // displayBudget so it matches what the user sees.
      setSelectedRows(displayBudget.map(b => b.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    const ids = [...selectedRows];
    if (ids.length === 0) { setBulkDeleteOpen(false); return; }
    // Optimistic update — yank the rows immediately so the totals refresh.
    setBudget(prev => prev.filter(b => !ids.includes(b.id)));
    setSelectedRows([]);
    setBulkDeleteOpen(false);
    try {
      const res = await axios.post(`${api}/budget/bulk-delete`, { ids });
      const deleted = Number(res.data?.deleted ?? 0);
      if (deleted !== ids.length) {
        // Backend reported a mismatch — surface that honestly instead of a
        // false-positive success toast.
        toast.error(`Backend reported ${deleted} deleted out of ${ids.length} requested. Refreshing…`);
      } else {
        toast.success(`${deleted} entries deleted`);
      }
      // Force a hard refetch so the table renders the true backend state —
      // not stale local data. `refresh()` also recomputes the summary cards
      // (Income / Bills / Debt Payments / Net Balance) from the fresh rows.
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete entries");
      await refresh();
    }
  };

  const handleApplyMinimums = async () => {
    const unpaid = budget.filter(b => b.category === "debt_payment" && !(b.actual > 0) && b.budgeted > 0);
    if (unpaid.length === 0) {
      toast.info("All debt payments for this month are already filled in.");
      return;
    }
    const total = unpaid.reduce((s, b) => s + (b.budgeted || 0), 0);
    if (!window.confirm(
      `Apply minimum payments to ${unpaid.length} debt${unpaid.length === 1 ? "" : "s"} for ${formatMonthDisplay(selectedMonth)}?\n\n` +
      `Total: ${fmt(total)}\n\n` +
      `Each debt's current balance will drop by its statement minimum. ` +
      `Rows that already have an actual payment will be skipped.`,
    )) return;
    try {
      const res = await axios.post(`${api}/budget/apply-minimums?month=${selectedMonth}`);
      toast.success(`Applied ${res.data.applied} payment${res.data.applied === 1 ? "" : "s"} totalling ${fmt(res.data.total)}`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply minimums"); }
  };

  const totals = useMemo(() => {
    let income = 0, expense = 0, debtPay = 0, budgetedIncome = 0, budgetedExpense = 0, budgetedDebt = 0;
    budget.forEach(b => {
      const amount = b.actual > 0 ? b.actual : b.budgeted;
      if (b.category === "income") { income += amount; budgetedIncome += b.budgeted; }
      else if (b.category === "debt_payment") { debtPay += amount; budgetedDebt += b.budgeted; }
      else { expense += amount; budgetedExpense += b.budgeted; }
    });
    return { income, expense, debtPay, budgetedIncome, budgetedExpense, budgetedDebt };
  }, [budget]);

  const net = totals.income - totals.expense - totals.debtPay;

  // Display-only ordering. When dateSort is null, use the server sort_order (preserves DnD).
  // When dateSort is set, render sorted by date but sort_order on server is untouched.
  const displayBudget = useMemo(() => {
    if (!dateSort) return budget;
    const arr = [...budget];
    arr.sort((a, b) => {
      const cmp = (a.date || "").localeCompare(b.date || "");
      return dateSort === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [budget, dateSort]);

  const cycleDateSort = () => {
    // manual → desc → asc → manual
    setDateSort((d) => (d === null ? "desc" : d === "desc" ? "asc" : null));
  };

  return (
    <div data-testid="monthly-budget-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight">Monthly Budget</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Unified cashflow — income, bills & debt payments</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="export-budget-csv-btn" variant="outline" onClick={exportBudgetCSV} className="rounded-sm gap-1.5 text-xs">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /><span className="hidden sm:inline">Export CSV</span>
          </Button>
          {!isReadOnly && (
            <Button data-testid="import-budget-csv-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-sm gap-1.5 text-xs">
              <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /><span className="hidden sm:inline">Import CSV</span>
            </Button>
          )}
          {!isReadOnly && (
            <Button data-testid="add-entry-btn" onClick={handleAdd} className="rounded-sm bg-black text-white hover:bg-gray-800 gap-2 mobile-fab">
              <Plus className="w-4 h-4" strokeWidth={1.5} /><span>Add Entry</span>
            </Button>
          )}
        </div>
      </div>

      {/* Month Selector */}
      <div className="flex items-center justify-center gap-3 border border-gray-200 rounded-sm py-3 px-4" style={{ background: "var(--surface-alt)" }}>
        <button data-testid="month-prev" onClick={() => setSelectedMonth(shiftMonth(selectedMonth, -1))} className="p-1 hover:bg-gray-100 rounded-sm">
          <ChevronLeft className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
        </button>
        <div className="text-center min-w-[180px]">
          <h2 className="font-heading text-lg font-semibold tracking-tight" data-testid="month-display">{formatMonthDisplay(selectedMonth)}</h2>
          {selectedMonth === currentMonth && <span className="text-[10px] text-blue-600 uppercase font-semibold">Current Month</span>}
          {isPastMonth && !pastEditUnlocked && <span className="text-[10px] text-gray-400 uppercase font-semibold">Read Only</span>}
          {isPastMonth && pastEditUnlocked && <span className="text-[10px] text-amber-600 uppercase font-semibold">Editing Past Month</span>}
          <div data-testid="budget-build-tag" className="text-[9px] text-gray-300 font-mono mt-0.5" title="Build tag — if you don't see this, your VPS is serving a stale bundle">
            {BUDGET_BUILD_TAG} · canEdit={String(canEditRows)}
          </div>
        </div>
        <button data-testid="month-next" onClick={() => setSelectedMonth(shiftMonth(selectedMonth, 1))} className="p-1 hover:bg-gray-100 rounded-sm">
          <ChevronRight className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
        </button>
        {isPastMonth && (
          <div className="ml-4 border-l border-gray-200 pl-4">
            <Button
              data-testid={pastEditUnlocked ? "lock-past-month-btn" : "unlock-past-month-btn"}
              variant={pastEditUnlocked ? "default" : "outline"}
              onClick={() => {
                if (pastEditUnlocked) {
                  setPastEditUnlocked(false);
                } else {
                  setUnlockDialogOpen(true);
                }
              }}
              className={`rounded-sm gap-1.5 text-xs ${pastEditUnlocked ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
              title={pastEditUnlocked ? "Click to re-lock this past month" : "Unlock this past month for corrections"}
            >
              {pastEditUnlocked
                ? <><LockOpen className="w-3.5 h-3.5" strokeWidth={1.5} />Re-lock Month</>
                : <><Lock className="w-3.5 h-3.5" strokeWidth={1.5} />Unlock for Editing</>}
            </Button>
          </div>
        )}
        {!isReadOnly && (
          <div className="flex gap-2 ml-4 border-l border-gray-200 pl-4">
            <Button variant="outline" onClick={handleCopyFromLast} className="rounded-sm gap-1.5 text-xs" data-testid="copy-last-month">
              <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />Copy Last Month
            </Button>
            <Button variant="outline" onClick={handleApplyMinimums} className="rounded-sm gap-1.5 text-xs" data-testid="apply-minimums-btn" title="One-tap fill: set every unpaid debt row's actual to its statement minimum and apply to debt balances.">
              <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />Apply Minimums
            </Button>
            <Button variant="outline" onClick={handleSaveTemplate} className="rounded-sm gap-1.5 text-xs" data-testid="save-template">
              <Save className="w-3.5 h-3.5" strokeWidth={1.5} />Save Template
            </Button>
          </div>
        )}
      </div>

      {/* Editing-past-month warning banner */}
      {isPastMonth && pastEditUnlocked && (
        <div data-testid="past-month-warning-banner" className="flex items-start gap-3 border border-amber-300 bg-amber-50 rounded-sm px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.75} />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Editing past month — {formatMonthDisplay(selectedMonth)}</p>
            <p className="text-amber-800 text-xs mt-0.5">Edits or deletes will recompute debt balances, the payoff plan, and Payment View history. Click <strong>Re-lock Month</strong> when done.</p>
          </div>
        </div>
      )}

      {/* Unlock confirmation dialog (replaces unreliable window.confirm) */}
      <Dialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen}>
        <DialogContent data-testid="unlock-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" strokeWidth={1.75} />Unlock past month?</DialogTitle>
            <DialogDescription className="pt-2 text-sm text-gray-700">
              You are about to edit <strong>{formatMonthDisplay(selectedMonth)}</strong> — a past month.<br /><br />
              Any edits or deletes will recompute synced data: debt balances in the Debt Tracker, the Payoff Plan projection, and Payment View history all update automatically.<br /><br />
              You can re-lock at any time using the same button.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button data-testid="unlock-cancel" variant="outline" onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
            <Button
              data-testid="unlock-confirm"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => {
                // eslint-disable-next-line no-console
                console.log("[VAULT/BUDGET] unlock-confirm clicked — setting pastEditUnlocked = true", { selectedMonth });
                setPastEditUnlocked(true);
                setUnlockDialogOpen(false);
                toast.success(`${formatMonthDisplay(selectedMonth)} unlocked for editing`);
              }}
            >
              <LockOpen className="w-4 h-4 mr-1.5" strokeWidth={1.75} />Unlock for Editing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk multi-select action bar — appears when 1+ rows selected. */}
      {selectedRows.length > 0 && canEditRows && (
        <div data-testid="bulk-action-bar" className="flex items-center justify-between gap-3 border border-gray-200 rounded-sm bg-white px-4 py-2.5">
          <span data-testid="bulk-selected-count" style={{ fontSize: 13, fontWeight: 500 }}>
            {selectedRows.length} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-clear-btn"
              onClick={() => setSelectedRows([])}
              className="rounded-sm text-xs"
              style={{
                background: "#fff", color: "#64748b",
                border: "1px solid #e2e8f0",
                borderRadius: 6, padding: "8px 14px",
                fontSize: 13, fontWeight: 500, cursor: "pointer",
              }}
            >Clear selection</button>
            <button
              data-testid="bulk-delete-btn"
              onClick={handleBulkDelete}
              style={{
                background: "#fee2e2", color: "#dc2626",
                border: "1px solid #fca5a5",
                borderRadius: 6, padding: "8px 16px",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#fecaca"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
            >Delete selected</button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <div className="bg-white border border-green-200 rounded-sm p-3">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Income</span>
          <p className="font-mono-data text-lg font-semibold text-green-700 mt-1">{fmt(totals.income || totals.budgetedIncome)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-3">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Bills</span>
          <p className="font-mono-data text-lg font-semibold text-red-700 mt-1">{fmt(totals.expense || totals.budgetedExpense)}</p>
        </div>
        <div className="bg-white border border-blue-200 rounded-sm p-3">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Debt Payments</span>
          <p className="font-mono-data text-lg font-semibold text-blue-700 mt-1">{fmt(totals.debtPay || totals.budgetedDebt)}</p>
        </div>
        <div className={`bg-white border rounded-sm p-3 ${net >= 0 ? "border-green-200" : "border-red-200"}`}>
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Net Balance</span>
          <p className={`font-mono-data text-lg font-semibold mt-1 ${net >= 0 ? "text-green-700" : "text-red-700"}`}>{net >= 0 ? "" : "-"}{fmt(net)}</p>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><p className="text-gray-400 text-sm">Loading...</p></div>
      ) : (
        <div className="border border-gray-200 rounded-sm overflow-x-auto custom-scroll">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className="spreadsheet-table w-full">
            <thead>
              <tr>
                <th className="w-8 text-center">
                  {canEditRows && (
                    <input
                      type="checkbox"
                      data-testid="select-all-budget"
                      checked={displayBudget.length > 0 && selectedRows.length === displayBudget.length}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      className="cursor-pointer"
                      aria-label="Select all rows"
                    />
                  )}
                </th>
                <th className="w-8"></th>
                <th
                  className="text-left cursor-pointer select-none hover:bg-gray-100"
                  style={{width: "100px"}}
                  onClick={cycleDateSort}
                  title="Click to sort by date (newest first → oldest first → manual order)"
                  data-testid="budget-date-sort"
                >
                  Date {dateSort === "desc" ? "↓" : dateSort === "asc" ? "↑" : ""}
                </th>
                <th className="text-left">Description</th>
                <th className="text-center" style={{width: "110px"}}>Category</th>
                <th className="text-right" style={{width: "100px"}}>Budgeted</th>
                <th className="text-right" style={{width: "100px"}}>Actual</th>
                <th className="text-right" style={{width: "90px"}}>Variance</th>
                <th className="text-right" style={{width: "110px"}}>Running Bal</th>
                <th className="text-left" style={{width: "100px"}}>Debt Link</th>
                <th className="text-right" style={{width: "100px"}}>Debt Bal</th>
                <th className="text-center" style={{width: "60px"}}>Actions</th>
              </tr>
            </thead>
            <SortableContext items={displayBudget.map(b => b.id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {displayBudget.length === 0 && (
                <tr><td colSpan={12} className="text-center text-gray-400 py-8">No entries for {formatMonthDisplay(selectedMonth)}.</td></tr>
              )}
              {displayBudget.map((b) => {
                const config = categoryConfig[b.category] || categoryConfig.expense;
                const Icon = config.icon;
                // Force fresh row mount whenever the unlock state flips so no
                // stale closure can hide the action buttons for bank-synced
                // (TrueLayer Replace-imported) rows after "Unlock for Editing".
                return <SortableBudgetRow key={`${b.id}-${canEditRows ? "rw" : "ro"}`} b={b} config={config} Icon={Icon} fmt={fmt} handleEdit={handleEdit} handleDelete={handleDelete} canEdit={canEditRows} dndDisabled={!!dateSort} selected={selectedRows.includes(b.id)} onToggleSelect={toggleSelectRow} />;
              })}
              {displayBudget.length > 0 && (
                <tr className="bg-gray-50 font-semibold">
                  <td></td><td></td><td></td>
                  <td className="text-xs uppercase tracking-wider text-gray-600">TOTALS <span className="text-[10px] text-gray-400 normal-case font-normal">(outflows only)</span></td>
                  <td></td>
                  <td className="numeric">{fmt(totals.budgetedExpense + totals.budgetedDebt)}</td>
                  <td className="numeric">{fmt(totals.expense + totals.debtPay)}</td>
                  <td></td>
                  <td className={`numeric ${net >= 0 ? "text-green-700" : "text-red-700"}`}>{net >= 0 ? "" : "-"}{fmt(net)}</td>
                  <td></td><td></td><td></td>
                </tr>
              )}
            </tbody>
            </SortableContext>
          </table>
          </DndContext>
        </div>
      )}

      {dialogOpen && (
        <BudgetDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSave} initialData={editEntry} debtNames={debtNames} />
      )}

      <BudgetImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={refresh}
        api={api}
        targetMonth={selectedMonth}
        debtNames={debtNames}
      />

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Budget Entry?"
        message={`Are you sure you want to delete "${deleteTarget?.description}"? This action cannot be undone.`}
        itemName={deleteTarget?.description}
        requireTypeName={false}
      />

      {/* Save Template — two-choice dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent data-testid="template-choice-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save budget template</DialogTitle>
            <DialogDescription className="pt-2 text-sm text-gray-700">
              Choose how to set up your recurring template — used to auto-seed future months.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <button
              data-testid="template-save-current"
              onClick={() => saveTemplateAs("current")}
              className="w-full text-left border border-gray-200 rounded-sm px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="font-semibold text-sm">Save current entries as template</div>
              <div className="text-xs text-gray-500 mt-0.5">Snapshot the {formatMonthDisplay(selectedMonth)} budget as your recurring template.</div>
            </button>
            <button
              data-testid="template-start-blank"
              onClick={() => saveTemplateAs("blank")}
              className="w-full text-left border border-gray-200 rounded-sm px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="font-semibold text-sm">Start blank template</div>
              <div className="text-xs text-gray-500 mt-0.5">Empty template — build your ideal recurring budget from scratch via Add Entry.</div>
            </button>
          </div>
          <DialogFooter>
            <Button data-testid="template-cancel" variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent data-testid="bulk-delete-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" strokeWidth={1.75} />
              Delete {selectedRows.length} entries?
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm text-gray-700">
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button data-testid="bulk-delete-cancel" variant="outline" onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
            <Button
              data-testid="bulk-delete-confirm"
              onClick={confirmBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.75} />Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
